/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Button from "../../ui/Button";
import ModalInput from "../../ui/ModalTextInput";
import Dropdown from "../../ui/SelectDropdown";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import { addAdminValidationSchema } from "./schemas";
import { AddAdminFormData, AddAdminModalProps } from "./types";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import { filterArray, mapDataToLabelValue } from "../../../helpers";
import {
  fetchAllCouriers,
  fetchCourierOffice,
} from "../../../redux/features/CourrierSlice";
import { createAdminAsync } from "../../../redux/features/adminSlice";

const AddAdminModal: React.FC<AddAdminModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const { roles } = useAppSelector((state) => state.admin);
  const [formData, setFormData] = useState<AddAdminFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    gender: "",
    address: "",
    role: "",
    offices: "",
    username: "",
    password: "",
    confirmPassword: "",
    country: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [courierId, setCourierId] = useState("");
  const { countries } = useAppSelector((state: RootState) => state.util);
  const { courierOffices, couriers } = useAppSelector(
    (state: RootState) => state.courier
  );
  const { isLoading } = useAppSelector((state: RootState) => state.admin);
  const dispatch = useAppDispatch();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear the error for this field when the user types
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: "", // Clear error for the specific input
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });

    // Clear the error for this field when an option is selected
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: "", // Clear error for the specific select field
    }));
  };

  const handleDiscard = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      gender: "",
      address: "",
      role: "",
      offices: "",
      username: "",
      password: "",
      confirmPassword: "",
      country: "",
    });
    onClose();
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Validate form using Yup
      await addAdminValidationSchema.validate(formData, { abortEarly: false });

      dispatch(
        createAdminAsync({
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          email: formData?.email,
          phoneNumber: formData?.phoneNumber,
          gender: formData?.gender,
          address: formData?.address,
          role: formData?.role,
          courierOfficeId:
            formData.role === "COURIER_ADMIN" ||
            formData.role === "COURIER_AGENT"
              ? formData?.offices
              : "",
          username: formData?.username,
          password: formData?.password,
        })
      )
        .unwrap()
        .then(() => {
          handleDiscard();
          onSave();
        })
        .catch((error) => {
          console.error("Failed to create admin:", error);
          // Optionally handle error (e.g., show a notification or error message)
        });
    } catch (err: any) {
      // Handle validation errors
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors); // Set validation errors in state
    }
  };

  useEffect(() => {
    dispatch(fetchAllCouriers({}));
  }, [formData.country, dispatch]);

  useEffect(() => {
    dispatch(fetchCourierOffice({ courierId, countryId: formData.country }));
  }, [courierId, dispatch]);

  const filteredRoles = filterArray(roles, ["COURIER_AGENT"]);

  console.log(roles)

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Add Admin"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={handleDiscard}
            label="Discard"
            layout="inline" variant="ghost"
          />
          <Button
            label="Done"
            type="submit"
            layout="inline"
            onClick={handleSubmit}
            disabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      }
      open={open}
      onClose={handleDiscard}
    >
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <ModalInput
            label="First Name"
            name="firstName"
            placeholder="Enter first name"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            error={errors.firstName}
          />

          <ModalInput
            label="Last Name"
            name="lastName"
            placeholder="Enter last name"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            error={errors.lastName}
          />

          <ModalInput
            label="Email Address"
            name="email"
            type="email"
            placeholder="Enter email address"
            value={formData.email}
            onChange={handleInputChange}
            required
            error={errors.email}
          />

          <ModalInput
            label="Phone Number"
            name="phoneNumber"
            type="tel"
            placeholder="Enter phone number"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            required
            error={errors.phoneNumber}
          />

          <Dropdown
            label="Gender"
            name="gender"
            options={["Male", "Female", "Other"]}
            required={true}
            value={formData.gender}
            onSelect={(value) => handleSelectChange("gender", value)}
            error={errors.gender}
            placeholder="Select gender"
          />

          <ModalInput
            label="Address"
            name="address"
            placeholder="Enter address"
            value={formData.address}
            onChange={handleInputChange}
            required
            error={errors.address}
          />

          <Dropdown
            label="Role"
            name="role"
            options={filteredRoles?.map((role) => {
              return { label: role, value: role };
            })}
            required={true}
            value={formData.role}
            onSelect={(value) => handleSelectChange("role", value)}
            error={errors.role}
            placeholder="Select role"
          />

          {(formData.role === "COURIER_ADMIN" ||
            formData.role === "COURIER_AGENT") && (
            <>
              <Dropdown
                label="Country"
                name="country"
                options={mapDataToLabelValue(countries, "name", "id")}
                value={formData.country}
                onSelect={(value) => {
                  setCourierId("");
                  handleSelectChange("offices", "");
                  handleSelectChange("country", value);
                }}
                error={errors.country}
                placeholder="Select country"
              />
              <Dropdown
                label="Courier"
                options={mapDataToLabelValue(
                  couriers?.data?.items,
                  "name",
                  "id"
                )}
                value={courierId}
                onSelect={(value) => setCourierId(value)}
                placeholder="Select Courier"
              />
              <Dropdown
                label="Offices"
                name="offices"
                options={mapDataToLabelValue(
                  courierOffices?.data?.data,
                  "name",
                  "id"
                )}
                required={true}
                value={formData.offices}
                onSelect={(value) => handleSelectChange("offices", value)}
                error={errors.offices}
                placeholder="Select office"
              />{" "}
            </>
          )}

          <ModalInput
            label="Username"
            name="username"
            placeholder="Enter username"
            value={formData.username}
            onChange={handleInputChange}
            required
            error={errors.username}
          />

          <ModalInput
            label="Password"
            name="password"
            type="password"
            placeholder="Enter password"
            value={formData.password}
            onChange={handleInputChange}
            required
            error={errors.password}
          />

          <ModalInput
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            placeholder="Confirm password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            error={errors.confirmPassword}
          />
        </form>
      </div>
    </CustomModalContainer>
  );
};

export default AddAdminModal;
